import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { apiClient } from "../../utils/axios";

const loadCodeListsRequest = createAsyncThunk('codelist/loadCodeListsRequest', async (_, thunkAPI) => {
  try {
    const results = await Promise.allSettled([
      apiClient.get('/codelist/countries'),
      apiClient.get('/codelist/tax-schemes'),
      apiClient.get('/codelist/electronic-address-schemes'),
      apiClient.get('/codelist/tax-categories'),
      apiClient.get('/codelist/payment-means-modes'),
      apiClient.get('/codelist/unit-of-measure-codes'),
      apiClient.get('/codelist/currencies'),
      apiClient.get('/codelist/charge-reason-codes'),
      apiClient.get('/codelist/discount-reason-codes'),
      apiClient.get('/codelist/tax-exemption-reason-codes'),
      apiClient.get('/codelist/zero-rating-reason-codes'),
      apiClient.get('/codelist/document-reason-codes'),
      apiClient.get('/codelist/registration-types'),
      apiClient.get('/codelist/transaction-type-codes'),
      apiClient.get("/codelist/omn-subdivisions"),
      apiClient.get('/codelist/service-type-codes'),
      apiClient.get('/codelist/item-classification-schemes'),
      apiClient.get('/codelist/incoterm-codes'),
      apiClient.get('/codelist/profit-margin-item-types')
    ]);
    const [
      countryListRes,
      taxSchemesRes,
      electronicAddressSchemesRes,
      taxCategoriesRes,
      paymentMeansModesRes,
      unitOfMeasureCodesRes,
      currenciesRes,
      chargeReasonCodesRes,
      discountReasonCodesRes,
      taxExemptionReasonCodesRes,
      zeroRateReasonCodesRes,
      documentReasonCodesRes,
      registrationTypesRes,
      transactionTypesRes,
        omnSubdivisionsRes,
      serviceTypeCodesRes,
      itemClassificationSchemeRes,
      incotemCodesRes,
      profitMarginItemTypesRes
      ] = results;

      return {
        countryList:
          countryListRes.status === "fulfilled" && countryListRes.value.status
            ? countryListRes.value.data
            : {},
        taxSchemesList:
          taxSchemesRes.status === "fulfilled" && taxSchemesRes.value.status
            ? taxSchemesRes.value.data
            : [],
        electronicAddressSchemesList:
          electronicAddressSchemesRes.status === "fulfilled" &&
          electronicAddressSchemesRes.value.status
            ? electronicAddressSchemesRes.value.data
            : {},
        taxCategoriesList:
          taxCategoriesRes.status === "fulfilled" &&
          taxCategoriesRes.value.status
            ? taxCategoriesRes.value.data
            : [],
        paymentMeansModesList:
          paymentMeansModesRes.status === "fulfilled" &&
          paymentMeansModesRes.value.status
            ? paymentMeansModesRes.value.data
            : {},
        unitOfMeasureCodesList:
          unitOfMeasureCodesRes.status === "fulfilled" &&
          unitOfMeasureCodesRes.value.status
            ? unitOfMeasureCodesRes.value.data
            : [],
        currenciesList:
          currenciesRes.status === "fulfilled" && currenciesRes.value.status
            ? currenciesRes.value.data
            : [],
        chargeReasonCodesList:
          chargeReasonCodesRes.status === "fulfilled" &&
          chargeReasonCodesRes.value.status
            ? chargeReasonCodesRes.value.data
            : [],
        discountReasonCodesList:
          discountReasonCodesRes.status === "fulfilled" &&
          discountReasonCodesRes.value.status
            ? discountReasonCodesRes.value.data
            : [],
        taxExemptionReasonCodesList:
          taxExemptionReasonCodesRes.status === "fulfilled" &&
          taxExemptionReasonCodesRes.value.status
            ? taxExemptionReasonCodesRes.value.data
            : [],
        zeroRatedReasonCodesList:
          zeroRateReasonCodesRes.status === "fulfilled" &&
          zeroRateReasonCodesRes.value.status
            ? zeroRateReasonCodesRes.value.data
            : [],
        documentReasonCodesList:
          documentReasonCodesRes.status === "fulfilled" &&
          documentReasonCodesRes.value.status
            ? documentReasonCodesRes.value.data
            : [],
        registrationTypesList:
          registrationTypesRes.status === "fulfilled" &&
          registrationTypesRes.value.status
            ? registrationTypesRes.value.data
            : {},
        transactionTypeCodesList:
          transactionTypesRes.status === "fulfilled" &&
          transactionTypesRes.value.status
            ? transactionTypesRes.value.data
            : [],
        omnSubdivisionList:
          omnSubdivisionsRes.status === "fulfilled" &&
          omnSubdivisionsRes.value.status
            ? omnSubdivisionsRes.value.data
            : [],
          serviceTypeCodesList:
          serviceTypeCodesRes.status === "fulfilled" &&
              serviceTypeCodesRes.value.status
          ? serviceTypeCodesRes.value.data
              : [],
          itemClassificationSchemesList:
              itemClassificationSchemeRes.status === "fulfilled" &&
              itemClassificationSchemeRes.value.status
                  ? itemClassificationSchemeRes.value.data
                  : [],
          incotermsCodesList:
              incotemCodesRes.status === "fulfilled" &&
              incotemCodesRes.value.status
                  ? incotemCodesRes.value.data
                  : [],
          profitMarginItemTypesList:
              profitMarginItemTypesRes.status === "fulfilled" &&
              profitMarginItemTypesRes.value.status
                  ? profitMarginItemTypesRes.value.data
                  : [],
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const initialState = {
  countryList: {},
  taxSchemesList: [],
  electronicAddressSchemesList: {},
  taxCategoriesList: [],
  paymentMeansModesList: {},
  unitOfMeasureCodesList: [],
  currenciesList: [],
  chargeReasonCodesList: [],
  discountReasonCodesList: [],
  taxExemptionReasonCodesList: [],
  zeroRatedReasonCodesList: [],
  documentReasonCodesList: [],
  registrationTypesList: {},
  transactionTypeCodesList: [],
  omnSubdivisionList: [],
  serviceTypeCodesList: [],
  itemClassificationSchemesList: [],
  incotermsCodesList: [],
  profitMarginItemTypesList: [],
  loading: false,
  error: null,
};

const codeListsSlice = createSlice({
  name: "codeLists",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
        .addCase(loadCodeListsRequest.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(loadCodeListsRequest.fulfilled, (state, action) => {
          state.countryList = action.payload.countryList;
          state.taxSchemesList = action.payload.taxSchemesList;
          state.electronicAddressSchemesList = action.payload.electronicAddressSchemesList;
          state.taxCategoriesList = action.payload.taxCategoriesList;
          state.paymentMeansModesList = action.payload.paymentMeansModesList;
          state.unitOfMeasureCodesList = action.payload.unitOfMeasureCodesList;
          state.currenciesList = action.payload.currenciesList;
          state.chargeReasonCodesList = action.payload.chargeReasonCodesList;
          state.discountReasonCodesList = action.payload.discountReasonCodesList;
          state.taxExemptionReasonCodesList = action.payload.taxExemptionReasonCodesList;
          state.zeroRatedReasonCodesList = action.payload.zeroRatedReasonCodesList;
          state.documentReasonCodesList = action.payload.documentReasonCodesList;
          state.registrationTypesList = action.payload.registrationTypesList;
          state.transactionTypeCodesList = action.payload.transactionTypeCodesList;
            state.omnSubdivisionList = action.payload.omnSubdivisionList;
          state.serviceTypeCodesList = action.payload.serviceTypeCodesList;
          state.itemClassificationSchemesList = action.payload.itemClassificationSchemesList;
          state.incotermsCodesList = action.payload.incotermsCodesList;
          state.profitMarginItemTypesList = action.payload.profitMarginItemTypesList;
          state.loading = false;
        })
        .addCase(loadCodeListsRequest.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Unknown error';
        });
  },
});

export default codeListsSlice.reducer;

const countryListState = (state) => state.codeLists?.countryList ?? [];
const unitOfMeasureCodesListState = (state) =>
  state.codeLists?.unitOfMeasureCodesList ?? [];
const chargeReasonCodesListState = (state) =>
  state.codeLists?.chargeReasonCodesList ?? [];
const discountReasonCodesListState = (state) =>
  state.codeLists?.discountReasonCodesList ?? [];
const taxExemptionReasonCodesListState = (state) =>
  state.codeLists?.taxExemptionReasonCodesList ?? [];
const zeroRatedRateReasonCodesListState = (state) =>
  state.codeLists?.zeroRatedReasonCodesList ?? [];
const taxCategoriesListState = (state) =>
  state.codeLists?.taxCategoriesList ?? [];
const registrationTypeListState = (state) =>
  state.codeLists?.registrationTypesList ?? [];
const electronicAddressTypeListState = (state) =>
  state.codeLists?.electronicAddressSchemesList ?? [];
const transactionTypeCodesListState = (state) =>
  state.codeLists?.transactionTypeCodesList ?? [];
const omnSubdivisionListState = (state) =>
  state.codeLists?.omnSubdivisionList ?? [];
const serviceTypeCodesListState = (state) => state?.codeLists?.serviceTypeCodesList ?? [];
const itemClassificationSchemesListState = (state) => state?.codeLists?.itemClassificationSchemesList ?? [];
const incotermCodesListState = (state) => state?.codeLists?.incotermsCodesList ?? [];
const profitMarginItemTypesState = (state) => state?.codeLists?.profitMarginItemTypesList ?? [];


export const getCountryOptions = createSelector([countryListState], (countryList) =>
    Object.entries(countryList).map(([label, code]) => ({
      label: label,
      code: code,
    })),
);

export const getSubdivisionOptions = createSelector(
  [omnSubdivisionListState],
  (subdivisionList) =>
    subdivisionList.map((subdivision) => ({
      name: subdivision.name,
      code: subdivision.code,
    })),
);

export const getSubdivisionOptionByCode = (subdivisionCode) =>
  createSelector([getSubdivisionOptions], (subdivisionOptions) =>
    subdivisionOptions.find(
      (subdivision) => subdivision.code === subdivisionCode,
    ),
  );

export const getEndpointSchemeIdOptions = createSelector(
  [electronicAddressTypeListState],
  (endpointAddressList) =>
    Object.entries(endpointAddressList).map(([code, label]) => ({
      code: code,
      label: label,
    })),
);

export const getRegistrationTypeOptions = createSelector(
  [registrationTypeListState],
  (registrationTypes) =>
    Object.entries(registrationTypes).map(([label, { code, scope }]) => ({
      label: label,
      code: code,
      scope: scope,
    })),
);

export const getRegistrationTypeOptionsByScope = createSelector(
  [getRegistrationTypeOptions],
  (registrationTypes) => (scope) =>
    registrationTypes.filter((type) => type.scope === scope),
);

export const getCountryOptionByCode = (countryCode) =>
  createSelector([getCountryOptions], (countryList) =>
    countryList.find((country) => country.code === countryCode),
  );

export const getEndpointSchemeIdByCode = (endpointSchemeId) =>
  createSelector([getEndpointSchemeIdOptions], (endpointSchemeIdList) =>
    endpointSchemeIdList.find(
      (endpointSchemeID) => endpointSchemeID.code === endpointSchemeId,
    ),
  );

export const getUOMOptions = createSelector(
  [unitOfMeasureCodesListState],
  (uomList) => uomList,
);

export const getB2BRegistrationTypeOptionsExcludingOTH = createSelector(
  [getRegistrationTypeOptions],
  (registrationTypes) =>
    registrationTypes.filter(
      (type) => type.scope === "B2B" && type.code !== "OTHID",
    ),
);

export const getCompanyIdRegistrationTypeOption = createSelector(
  [getRegistrationTypeOptions],
  (registrationTypes) =>
    registrationTypes.find((type) => type.code === "OTHID"),
);

export const getUOMOptionByCode = (uomCode) =>
  createSelector([getUOMOptions], (uomOptions) =>
    uomOptions.find((uom) => uom.code === uomCode),
  );

export const getChargeReasonCodeOptions = createSelector(
  [chargeReasonCodesListState],
  (chargeReasonCodesList) =>
    chargeReasonCodesList.map((reasonCode) => ({
      label: reasonCode.reason || reasonCode.description || reasonCode.code,
      code: reasonCode.code,
    })),
);

export const getChargeReasonCodeOptionByCode = (reasonCode) =>
  createSelector([getChargeReasonCodeOptions], (chargeReasonCodeOptions) =>
    chargeReasonCodeOptions.find((reason) => reason.code === reasonCode),
  );

export const getDiscountReasonCodeOptions = createSelector(
  [discountReasonCodesListState],
  (discountReasonCodesList) =>
    discountReasonCodesList.map((reasonCode) => ({
      label: reasonCode.reason || reasonCode.description || reasonCode.code,
      code: reasonCode.code,
    })),
);

export const getDiscountReasonCodeOptionByCode = (reasonCode) =>
  createSelector([getDiscountReasonCodeOptions], (discountReasonCodeOptions) =>
    discountReasonCodeOptions.find((reason) => reason.code === reasonCode),
  );

export const getTaxExemptionReasonCodeOptions = createSelector(
  [taxExemptionReasonCodesListState],
  (taxExemptionReasonCodesList) =>
    taxExemptionReasonCodesList.map((reasonCode) => ({
      reason: reasonCode.reason,
      code: reasonCode.code,
    })),
);

export const getZeroRatedReasonCodeOptions = createSelector(
  [zeroRatedRateReasonCodesListState],
  (zeroRateReasonCodesList) =>
    zeroRateReasonCodesList.map((reasonCode) => ({
      reason: reasonCode.reason,
      code: reasonCode.code,
    })),
);

export const getTaxExemptionReasonCodeOptionByCode = (reasonCode) =>
  createSelector(
    [getTaxExemptionReasonCodeOptions],
    (taxExemptionReasonCodeOptions) =>
      taxExemptionReasonCodeOptions.find(
        (reason) => reason.code === reasonCode,
      ),
  );

export const getZeroRatedReasonCodeOptionByCode = (reasonCode) =>
  createSelector(
    [getZeroRatedReasonCodeOptions],
    (zeroRatedReasonCodeOptions) =>
      zeroRatedReasonCodeOptions.find((reason) => reason.code === reasonCode),
  );

export const getTaxCategoryOptions = createSelector(
  [taxCategoriesListState],
  (taxCategoriesList) =>
    taxCategoriesList.map((taxCategory) => ({
      label: taxCategory.description,
      code: taxCategory.category,
      rate: taxCategory.rate,
    })),
);

export const getTaxCategoryOptionByCode = (taxCategoryCode) =>
  createSelector([getTaxCategoryOptions], (taxCategoryOptions) => {
    return taxCategoryOptions.find(
      (taxCategory) => taxCategory.code === taxCategoryCode,
    );
  });

export const getRegistrationTypeOptionByCode = (registrationTypeCode) =>
  createSelector([getRegistrationTypeOptions], (registrationTypeOptions) =>
    registrationTypeOptions.find(
      (regType) => regType.code === registrationTypeCode,
    ),
  );

export const getTransactionTypeOptions = createSelector(
  [transactionTypeCodesListState],
  (transactionTypeCodesList) => transactionTypeCodesList,
);


export const getServiceTypeCodesOptions = createSelector(
    [serviceTypeCodesListState],
    (serviceTypeCodesList) => serviceTypeCodesList.map(
        (serviceTypeCode) => ({
            code: serviceTypeCode.code,
            name: serviceTypeCode.name,
        })
    ),
);


export const getItemClassificationSchemeOptions = createSelector(
    [itemClassificationSchemesListState],
    (itemClassificationSchemeList) => itemClassificationSchemeList.map(
        (itemClassificationScheme) => ({
            code: itemClassificationScheme.code,
            description: itemClassificationScheme.description,
        })
    ),
);

export const getIncotermsCodes = createSelector(
    [incotermCodesListState],
    (incotermCodeList) => incotermCodeList.map(
        (incotermCodes) => ({
            code: incotermCodes.code,
            description: incotermCodes.description,
        })
    ),
);

export const getProfitMarginItemTypes = createSelector(
    [profitMarginItemTypesState],
    (itemTypesList) => itemTypesList.map(
        (itemTypes) => ({
            code: itemTypes.code,
            description: itemTypes.description,
        })
    ),
);

export { loadCodeListsRequest };
